/**
 * The wire shapes `Rws.AiInsights.Host.Mcp.McpConnectionEndpoints` returns: the AI applications the
 * signed-in person has connected to their account, and the call that disconnects one. A person only ever
 * sees and revokes their own. The endpoints exist only when the portal's MCP authorization server is
 * enabled, so a `404` means "not switched on here" rather than a failure.
 */
import { apiFetch } from "./apiFetch";

/** One application the person has connected. */
export interface McpConnection {
  id: string;
  clientId: string;
  clientName: string;
  scopes: string[];
  /** When the person approved the connection, or `null` when it was not recorded. */
  connectedAt: string | null;
}

/** The outcome of listing or revoking connections. */
export type McpConnectionsResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "unavailable" }
  | { kind: "error"; message: string };

const connectionsPath = "/api/mcp/connections";

async function connectionsRequest<TResponse>(path: string, init: RequestInit, label: string): Promise<McpConnectionsResult<TResponse>> {
  try {
    const response = await apiFetch(path, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" }, ...init });
    if (response.status === 404) {
      return { kind: "unavailable" };
    }
    if (!response.ok) {
      return { kind: "error", message: `The ${label} endpoint returned ${String(response.status)}.` };
    }
    if (response.status === 204) {
      return { kind: "ok", response: undefined as TResponse };
    }
    return { kind: "ok", response: (await response.json()) as TResponse };
  } catch {
    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}

/** Calls `GET /api/mcp/connections`. */
export async function fetchMcpConnections(): Promise<McpConnectionsResult<McpConnection[]>> {
  return connectionsRequest<McpConnection[]>(connectionsPath, { method: "GET" }, "connected applications");
}

/** Calls `POST /api/mcp/connections/{id}/revoke`; `unavailable` when the connection no longer exists. */
export async function revokeMcpConnection(id: string): Promise<McpConnectionsResult<undefined>> {
  return connectionsRequest<undefined>(`${connectionsPath}/${encodeURIComponent(id)}/revoke`, { method: "POST" }, "disconnect application");
}
